import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import { ManagementContractContext, updateManagementContractContext } from "@eres/ejari-core/contexts/ManagementContractContext";
import Strings from "@my-dld/assets/strings";
import FormSection from "@my-dld/components/Form/FormSection";
import FormAction from "@my-dld/components/Form/FormAction";
import FormRow from "@eres/forms-web/FormRow";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import { LoadableDataState, showErrorMessage } from "@my-dld/utils/UIUtils";
import { TenantDocumentTransformer } from "@eres/ejari-api/Utils";
import { EjariApi } from "@eres/ejari-api";
import { EjariPropertyUsage, TenantDocumentLookup } from "@eres/ejari-api/Entity";
import Dropzone from "react-dropzone";
import DocumentInputField from "@my-dld/components/Form/DocumentInputField";
import { LocaleUtil } from "@eres/core";
import { RenderManagementContractDocs } from "./RenderManagementContractDocs";
import { AttachmentInfo } from "@eres/ejari-api/Entity/tenant";

interface Props extends RoutedViewProps {
}

interface LookupState {
	documents?: TenantDocumentLookup[];
}

interface State extends LookupState, LoadableDataState {
}

class Documents extends Component<Props, State> {
	static contextType = ManagementContractContext;
	context!: React.ContextType<typeof ManagementContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			errorContainer: {},
			loadState: "loading",
		};
	}

	componentDidMount(): void {
		this.updateFromContext();
		this.loadLookup();
	}

	private loadLookup() {
		const param1 = this.context.properties?.[ 0 ].EjariPropertyUsageId === EjariPropertyUsage.RESIDENTIAL ? "35" : "36";
		EjariApi.getLookup( "GetAttachments", param1, undefined, TenantDocumentTransformer )
		.then( response => {
			this.setState( { documents: response, loadState: "done" }, () => {
				this.updateFromContext();
			} );
		} );
	}

	render() {
		const { documents, errorContainer } = this.state;
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-6 form-group",
			errorManager: defaultErrorManager,
		};
		return <div className="tender-registration-form">
			<FormSection title={ Strings.documents }>
				<FormRow { ...filedProps }>
					{ documents && documents.map( ( docLookup: TenantDocumentLookup ) => {
						const isDocsAdded = ( docLookup && docLookup.Docs !== undefined && docLookup.Docs.length > 0 );
						const maxPages = docLookup.MaxPages ? docLookup.MaxPages : 1;
						const maxSizeKb = docLookup.MaxSizeKb ? ( docLookup.MaxSizeKb * 1000 ) : ( 2 * 1024 * 1000 );
						return <div key={ `doc_info_${ docLookup.Id }` } className="col-md-6 form-group">
							<Dropzone
								multiple={ maxPages > 1 }
								maxSize={ maxSizeKb }
								onDropRejected={ this.onDropRejected }
								onDrop={ ( files: File[] ) => {
									this.onDrop( docLookup, files );
								} }
								accept={ [ "image/png", "image/jpeg", "application/pdf" ] }>
								{ ( { getRootProps, getInputProps } ) => (
									<DocumentInputField
										{ ...getRootProps() }
										required={ true }
										dropZoneInputProps={ getInputProps() }
										label={ LocaleUtil.getLocalValue( docLookup ) }
										isDocsAdded={ isDocsAdded }
										error={ errorContainer[ docLookup.Id ] }
										disabled={ ( docLookup.Docs && docLookup.Docs.length === maxPages ) }/>
								) }
							</Dropzone>
							<span>
                                <small>{ Strings.tabu_upload_documents_note }</small><br/>
                                <small>{ Strings.max_num_files + " : " + maxPages }</small>
                            </span>
							<RenderManagementContractDocs docId={ docLookup.Id } documents={ docLookup.Docs } onDelete={ this.onDelete }/>
						</div>;
					} ) }
				</FormRow>
			</FormSection>
			<FormRow>
				<FormAction
					primaryActionLabel={ Strings.submit }
					onPrimaryAction={ this.onSubmit }
					secondaryActionLabel={ Strings.back }
					onSecondaryAction={ this.onBack }/>
			</FormRow>
		</div>;
	}

	private updateFromContext() {
		const d = this.state.documents;
		const { documents } = this.context;
		const docs = d?.map( d => {
			d.Docs = documents?.find( a => a.Id === d.Id )?.Docs;
			return d;
		} );
		this.setState( { ...this.state, documents: docs } );
	}

	private onSubmit = () => {
		if ( this.isValid() ) {
			this.context.submitManagementContract( this.context ).then( () => {
			} )
			.catch( () => {
			} );
		}
	};

	private onBack = () => {
		this.props.history.goBack();
	};

	isValid = () => {
		const { documents } = this.state;
		let isValid = true;
		documents?.forEach( doc => {
			if ( doc.IsMandatory && ( doc.Docs == null || doc.Docs.length === 0 ) ) isValid = false;
		} );
		return isValid;
	};

	private onDropRejected = () => {
		showErrorMessage( Strings.tabu_file_exceed_size );
	};

	private onDrop = ( selDoc: TenantDocumentLookup, files: File[] ) => {
		if ( selDoc.MaxPages && selDoc.MaxPages > 1 ) {
			if ( selDoc.Docs && selDoc.Docs.length > 0 ) {
				let maxAllowed = ( selDoc.MaxPages - selDoc.Docs.length );
				if ( files.length > maxAllowed )
					files = files.splice( 0, maxAllowed );
			} else if ( files.length > selDoc.MaxPages ) {
				files = files.splice( 0, selDoc.MaxPages );
			}
		}
		files.filter( x => x.type.startsWith( "image/" ) || x.type.startsWith( "application/pdf" ) )
		.forEach( file => this.addFile( selDoc, file ) );
	};

	private addFile( selDoc: TenantDocumentLookup, file: File ) {
		const reader = new FileReader();
		reader.onload = () => {
			console.log( file );
			const binaryStr = reader.result;
			if ( selDoc.Docs == null )
				selDoc.Docs = [];
			const newDoc: AttachmentInfo = {
				DocumentID: selDoc.Id,
				Type: file.type,
				LocalData: binaryStr as string,
				FileName: file.name,
			};
			if ( selDoc.MaxPages != null && selDoc.MaxPages > 1 ) {
				selDoc.Docs.push( newDoc );
			} else {
				selDoc.Docs = [ newDoc ];
			}
			const info = this.state.documents;
			let error = this.state.errorContainer;
			delete error[ selDoc.Id ];
			const docs = info?.map( ( doc: TenantDocumentLookup ) => {
				return ( selDoc.Id === doc.Id ) ? selDoc : doc;
			} ) ?? [];
			this.setState( {
				documents: docs,
				errorContainer: error,
			} );
			updateManagementContractContext( { ...this.context, documents: docs } );
		};
		reader.readAsDataURL( file );
	}

	private onDelete = ( docId: number, index: number ) => {
		const { documents } = this.state;
		const d = documents?.map( d => d.Id === docId ? { ...d, Docs: d.Docs?.filter( ( _, i ) => i !== index ) } : d );
		updateManagementContractContext( { ...this.context, documents: d } );
		this.setState( {
			documents: d,
		} );
	};
}

export default Documents;
